import React from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Container,
  Avatar,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import { logout } from '../../store/slices/authSlice'
import { useTranslation } from 'react-i18next'
import { ChangePasswordModal } from '../auth/ChangePasswordModal'

export const PortalLayout: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false)

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
  }

  const userInitials = user
    ? `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase()
    : 'U'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      <AppBar position="sticky" elevation={2} sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                letterSpacing: 0.5,
                background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.7) 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('appTitle', 'Al Maysarah Portal')}
            </Typography>
          </Box>

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.875rem',
                    bgcolor: 'primary.dark',
                    color: 'primary.contrastText',
                    border: '2px solid rgba(255,255,255,0.2)',
                  }}
                >
                  {userInitials || <AccountCircleIcon size="small" />}
                </Avatar>
                {!isMobile && (
                  <Box sx={{ textAlign: 'start' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit', lineHeight: 1.2 }}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', textTransform: 'capitalize' }}>
                      {user.role}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Tooltip title={t('settings.changePassword', 'Change Password')}>
                <IconButton
                  color="inherit"
                  onClick={() => setChangePasswordOpen(true)}
                  aria-label={t('settings.changePassword', 'Change Password')}
                  size="small"
                >
                  <VpnKeyIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <IconButton
                color="inherit"
                onClick={handleLogout}
                title={t('auth.signOut', 'Sign Out')}
                edge="end"
                size="small"
                sx={{ ml: 0.5 }}
              >
                <LogoutIcon size="small" />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Container maxWidth="md" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', px: 0 }}>
          <Outlet />
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          &copy; {new Date().getFullYear()} Al Maysarah IM. All rights reserved.
        </Typography>
      </Box>
    </Box>
  )
}
export default PortalLayout
